import cuid from "cuid";
import slugify from "../utils/slugify";
import * as yup from "yup";
import {
  CountryCodes,
  Currencies,
  FeeStrategies,
  LicenseSegmentType,
  locales,
  paymentMethodTypes,
} from "../enums";
import { controlRoleSchema } from "./ControlRole";
import utcIsoDateString from "./Date";
import { itemCategorySchema } from "./ItemCategory";
import { yupMobileNumberField } from "./MobileInputForm";
import { sessionTypesArraySchema } from "./SessionTypes";

const PaymentMethodTypes = Object.values(paymentMethodTypes);

export const taxRuleSchema = yup.object({
  sessionTypes: sessionTypesArraySchema.notRequired().default(undefined),
  placeIds: yup.array().of(yup.string()).notRequired().default(undefined),
  categoryIds: yup.array().of(yup.string()).notRequired().default(undefined),

  orderItem: yup.boolean().notRequired().default(undefined),
  tip: yup.boolean().notRequired().default(undefined),
  serviceCharge: yup.boolean().notRequired().default(undefined),
  deliveryFee: yup.boolean().notRequired().default(undefined),
});

export type TaxRule = yup.InferType<typeof taxRuleSchema>;

export const taxSchema = yup.object({
  id: yup.string().required().transform(slugify).default(""),
  name: yup.string().required().default(""),
  rate: yup.number().required().min(0).max(1).default(0),
  included: yup.boolean().required(),
  active: yup.boolean().default(true),
  rules: yup.array().of(taxRuleSchema).nullable().default(null),
});

export const acceptSchema = yup
  .object({
    id: yup.string().default(cuid),
    method: yup
      .mixed<paymentMethodTypes>()
      .oneOf(PaymentMethodTypes)
      .transform((v) =>
        PaymentMethodTypes.includes(v) ? v : paymentMethodTypes.OTHER
      ),
    integrationId: yup.string().nullable(),
    operator: yup.object({ only: yup.boolean().default(false) }),
    name: yup.string().min(2).nullable(),
    active: yup.boolean().default(true),
    pinned: yup.boolean().default(false),
    parameters: yup.object({
      brands: yup.array().of(yup.string().min(1)).nullable().default(null),
    }),
  })
  .required();

export const FixAndPercentSchema = yup.object({
  fixed: yup.number().min(0),
  percent: yup.number().min(0).max(1),
});

export const oneTierSchema = yup.object({
  fee: FixAndPercentSchema.required(),
});

export const twoTierSchema = yup.object({
  low: FixAndPercentSchema.required(),
  high: FixAndPercentSchema.required(),
});

export type OneTierStrategy = yup.InferType<typeof oneTierSchema>;

export type TwoTierStrategy = yup.InferType<typeof twoTierSchema>;

export const segmentTermsSchema = yup
  .object({
    minMonthlyFee: yup.number().min(0),
    minMonthlyGraceMonths: yup.number().min(0),
    strategy: yup
      .mixed<FeeStrategies>()
      .oneOf(Object.values(FeeStrategies))
      .default(FeeStrategies.TWO_TIERS)
      .required(),
    struct: yup.mixed<OneTierStrategy | TwoTierStrategy>().default(
      twoTierSchema.cast({
        low: { fixed: 0.2, percent: 0.014 },
        high: { fixed: 0.2, percent: 0.025 },
      })
    ),
  })
  .test("validate struct against strategy", function (value) {
    switch (value.strategy) {
      case FeeStrategies.ONE_TIER:
        return oneTierSchema.isValidSync(value);
      case FeeStrategies.TWO_TIERS:
        return twoTierSchema.isValidSync(value);
      default:
        return this.createError({
          path: "strategy",
          message: "invalid strategy for given struct",
        });
    }
  });

export const licenseSegmentSchema = yup.object({
  id: yup.string().default(cuid),
  types: yup
    .array()
    .of(
      yup.mixed<LicenseSegmentType>().oneOf(Object.values(LicenseSegmentType))
    )
    .min(1)
    .required()
    .default([]),
  base: segmentTermsSchema.required(),
  gradual: yup
    .array()
    .of(
      segmentTermsSchema.concat(
        yup.object({
          minMonthlyVolume: yup.number(),
        })
      )
    )
    .optional(),
});

export const licenseSchema = yup.object({
  id: yup.string().nullable(),
  active: yup.boolean().default(true),
  notes: yup.string().optional().nullable(), //
  currency: yup.string().nullable(), //
  segments: yup.array().of(licenseSegmentSchema).min(1),
  validFrom: utcIsoDateString
    .required()
    .default(() => new Date().toISOString()),
  validThrough: utcIsoDateString.nullable(),
});

export const accountSchema = yup.object({
  id: yup.string().notRequired(), //
  name: yup.string().required().default(""),
  companyType: yup.string().required().default(""),
  companyId: yup.string().required().default(""),
  taxId: yup.string().required().default(""),
  meta: yup
    .object({
      state: yup.string().nullable(),
      city: yup.string().nullable(),
      zip: yup.string().nullable(),
    })
    .nullable(),
});

export const businessSchema = yup
  .object({
    id: yup.string(),
    name: yup.string().default("").min(2),
    currency: yup
      .string()
      .oneOf(Object.values(Currencies))
      .default(Currencies.ILS),
    active: yup.boolean().default(true),
    locale: yup.string().nullable().default(locales.en_US),
    country: yup
      .string()
      .default(CountryCodes.UnitedKingdom)
      .oneOf(Object.values(CountryCodes)),
    email: yup.string().email().notRequired(),
    website: yup.string().url().nullable(),
    mobile: yupMobileNumberField.notRequired(),
    roles: yup.array().of(controlRoleSchema),
    accounts: yup.array().of(accountSchema).min(1).defined().default([]),
    taxes: yup
      .array()
      .of(taxSchema)
      .min(0)
      .defined()
      .default([])
      .nullable()
      .test(function (value) {
        if (value && value.length) {
          const ids = value.map((tax) => tax.id);
          const set = new Set(ids);
          if (ids.length !== set.size) {
            for (let i in ids) {
              if (ids.filter((id) => id === ids[i])?.length > 1) {
                return this.createError({
                  path: `taxes[${i}].id`,
                  message: "tax id must be unique",
                });
              }
            }
          }
        }
        return true;
      }),
    accepts: yup.array().of(acceptSchema).defined().default([]),
    itemCategories: yup.array().of(itemCategorySchema),
    licenses: yup.array().of(licenseSchema).defined().default([]),
    createdAt: yup.date().nullable(),
    updatedAt: yup.date().nullable(),
  })
  .required();

export const businessWizardSchema = yup
  .object({
    roleName: yup.string().min(2).required(),
    pin: yup.string().min(4).max(4).required(),
    agree: yup.boolean().oneOf([true]).required().default(false),
  })
  .concat(businessSchema);

export type TBusiness = yup.InferType<typeof businessSchema>;
export type TBusinessWizard = yup.InferType<typeof businessWizardSchema>;
export type TBusinessAccept = yup.InferType<typeof acceptSchema>;
export type TBusinessTax = yup.InferType<typeof taxSchema>;
export type TBusinessTaxRule = yup.InferType<typeof taxRuleSchema>;
export type TBusinessAccount = yup.InferType<typeof accountSchema>;
export type TLicense = yup.InferType<typeof licenseSchema>;
export type TLicenseSegment = yup.InferType<typeof licenseSegmentSchema>;
export type TLicenseSegmentTerms = yup.InferType<typeof segmentTermsSchema>;
